import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Chip,
  IconButton,
  Box,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Cancel,
  CheckCircle,
  DirectionsCar,
  AssignmentInd
} from '@mui/icons-material';
import MovementTaskCompletionModal from '../../components/MovementTaskCompletionModal/MovementTaskCompletionModal';
import { useYardData } from '../YardManagement/yardDataFetch';

import { getAuthToken } from '../../utils/cookieUtils';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Types for task detail
interface TaskTemplate {
  uuid: string;
  name: string;
  task_template_json: any;
}

interface TaskType {
  uuid: string;
  type: string;
  class: string;
}

interface TaskStatus {
  uuid: string;
  status: string;
}

// Union types for location objects
interface DockObject {
  uuid: string;
  name: string;
  status?: string;
}

interface YardSpotObject {
  uuid: string;
  name: string;
  facilityZoneUuid?: string;
}

interface TrailerObject {
  uuid: string;
  number: string;
}

interface Trailer {
  trailer_number: string;
  carrier?: {
    name: string;
  };
}

interface Driver {
  firstName: string;
  lastName: string;
}


interface ModifiedByUser {
  email: string;
}

interface TaskDetail {
  uuid: string;
  data: any;
  due_at: string | null;
  completed_at: string | null;
  user_id: string | null;
  facility_id: string;
  /** Destination is only a zone and the facility requires a specific spot or dock to complete. */
  requires_destination_selection?: boolean | null;
  driver: Driver | null;
  modified_by_user: ModifiedByUser | null;
  driver_id: string | null;
  completed_by_user_id: string | null;
  completed_by_driver_id: string | null;
  modified_by_user_id: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  task_template: TaskTemplate | null;
  task_type: TaskType | null;
  task_status: TaskStatus | null;
  from_location: string | null;
  to_location: string | null;
  from_object: DockObject | YardSpotObject | TrailerObject | null;
  to_object: DockObject | YardSpotObject | TrailerObject | null;
  trailer: Trailer | null;
}

const TaskDetail = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  
  // State
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [completing, setCompleting] = useState<boolean>(false);
  const [completingMovement, setCompletingMovement] = useState<boolean>(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<TaskDetail | null>(null);
  
  // Get facility data for the modal
  const {
    facilities: taskDetailFacilities,
    facilityZones: taskDetailFacilityZones,
    yardSpots: taskDetailYardSpots,
    warehouseData: taskDetailWarehouseData
  } = useYardData(undefined, 60000);

  // Checklist completion state - maps task UUID to single boolean value
  const [checklistCompletionState, setChecklistCompletionState] = useState<Record<string, boolean>>({});

  // Check if task is a workflow with checklist
  const isWorkflowWithChecklist = (task: TaskDetail) => {
    return task.task_type?.type === 'WORKFLOW' && 
           task.data && 
           task.data.checklist && 
           Array.isArray(task.data.checklist) && 
           task.data.checklist.length > 0;
  };

  // Format workflow type (replace _ with space and title case)
  const formatWorkflowType = (workflowType: string) => {
    return workflowType
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
  };

  // Initialize checklist completion state for a task
  const initializeChecklistState = (taskUuid: string) => {
    if (checklistCompletionState[taskUuid] === undefined) {
      setChecklistCompletionState(prev => ({
        ...prev,
        [taskUuid]: false
      }));
    }
  };

  // Handle checkbox change for checklist completion
  const handleChecklistChange = (taskUuid: string, checked: boolean) => {
    setChecklistCompletionState(prev => ({
      ...prev,
      [taskUuid]: checked
    }));
  };

  // Check if checklist is completed for a task
  const isChecklistCompleted = (taskUuid: string) => {
    return checklistCompletionState[taskUuid] === true;
  };

  // Check if a workflow task can be completed
  const canCompleteWorkflowTask = (task: TaskDetail) => {
    if (!isWorkflowWithChecklist(task)) return true; // Non-checklist workflows can be completed normally
    return isChecklistCompleted(task.uuid);
  };

  // Helper function to get zone name from facilityZoneUuid
  const getZoneName = (facilityZoneUuid: string | undefined): string | null => {
    if (!facilityZoneUuid || !taskDetailFacilityZones) return null;
    
    const zone = taskDetailFacilityZones.find(
      (z: any) => z.facility_zone_uuid === facilityZoneUuid
    );
    
    return zone ? zone.facility_zone_name : null;
  };

  // Fetch task details using GraphQL
  const fetchTaskDetail = async () => {
    if (!taskId) return;
    
    try {
      setIsLoading(true);
      setError(null);

      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for task details
      const taskDetailQuery = `
        query GetTaskDetails($taskId: String!) {
          task(id: $taskId) {
            uuid
            data
            due_at
            completed_at
            user_id
            facility_id
            requires_destination_selection
            driver_id
            completed_by_user_id
            completed_by_driver_id
            modified_by_user_id
            metadata
            created_at
            updated_at
            driver {
              
              firstName
              lastName
            }
              modified_by_user {
                
                email
              }
            # Related entities
            task_template {
              uuid
              name
              task_template_json
              account_id
            }
            
            task_type {
              uuid
              type
              class
            }
            
            task_status {
              uuid
              status
            }
            
            from_location
            to_location
            from_object {
              ... on DockObject {
                uuid
                name
              }
              ... on YardSpotObject {
                uuid
                name
                facilityZoneUuid
              }
              ... on FacilityZoneObject {
                  uuid
                  name
              }
                ... on ExternalLocationObject {
        name
      }
             
            }
           to_object {
              ... on YardSpotObject {
                  uuid
                  name
         
              }
              ... on DockObject {
                  uuid
                  name
     
              }
              ... on WarehouseZoneObject {
                  uuid
                  name
 
              }
                  ... on ExternalLocationObject {
        name
      }
              ... on FacilityZoneObject {
                  uuid
                  name
              }
              }
            trailer {
              trailer_number
              carrier {
                  name
              }
            }
          }
        }
      `;

      // Execute the GraphQL query
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: taskDetailQuery,
          variables: {
            taskId: taskId
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      setTask(result.data.task);

      // Initialize checklist state for workflow task if it has a checklist
      if (result.data.task && isWorkflowWithChecklist(result.data.task)) {
        initializeChecklistState(result.data.task.uuid);
      }

    } catch (err) {
      console.error('Failed to fetch task details:', err);
      setError('Failed to fetch task details');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchTaskDetail();
  }, [taskId]);

  // Handle refresh
  const handleRefresh = () => {
    fetchTaskDetail();
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/tasks');
  };

  // Handle take task - navigate to TaskApp with deep link
  const handleTakeTask = () => {
    if (task?.data?.task_group_uuid) {
      navigate(`/taskapp?search=${task.data.task_group_uuid}`);
    } else {
      navigate('/taskapp');
    }
  };

  // Check if task is unassigned
  const isTaskUnassigned = () => {
    return task?.task_status?.status === 'UNASSIGNED';
  };

  // Handle cancel task
  const handleCancelTask = async () => {
    if (!task) return;
    
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to cancel this task?');
    if (!confirmed) {
      return;
    }
    
    setCancelling(true);
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid: task.uuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh task details
      await fetchTaskDetail();
    } catch (error) {
      console.error('Failed to cancel task:', error);
      setError('Failed to cancel task');
    } finally {
      setCancelling(false);
    }
  };

  // Handle complete task
  const handleCompleteTask = async () => {
    if (!task) return;
    
    setCompleting(true);
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const completeTaskMutation = `
        mutation CompleteTask($taskUuid: String!) {
          complete_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeTaskMutation,
          variables: { taskUuid: task.uuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh task details
      await fetchTaskDetail();
    } catch (error) {
      console.error('Failed to complete task:', error);
      setError('Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  // Handle complete movement task
  const handleCompleteMovementTask = async () => {
    if (!task) return;
    
    // Show confirmation modal
    setTaskToComplete(task);
    setConfirmationModalOpen(true);
  };
  
  // Handle movement task completion with destination overrides
  const handleCompleteMovementTaskWithDestination = async (destinationOverrides: any) => {
    if (!taskToComplete) return;
    
    setCompletingMovement(true);
    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { 
            input: { 
              taskUuid: taskToComplete.uuid,
              ...destinationOverrides
            } 
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh task details
      await fetchTaskDetail();
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      setError('Failed to complete movement task');
    } finally {
      setCompletingMovement(false);
      setTaskToComplete(null);
      setConfirmationModalOpen(false);
    }
  };

  // Handle clear lock
  // Get status color
  const getStatusColor = (status: string | undefined): "default" | "success" | "primary" | "error" | "secondary" | "info" | "warning" | undefined => {
    if (!status) return 'default';
    
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'active':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading task details...</Typography>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
        <Button onClick={handleRefresh} variant="contained">
          Retry
        </Button>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Tasks
        </Button>
      </div>
    );
  }

  // Task not found
  if (!task) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" sx={{ mb: 2 }}>Task not found</Typography>
        <Button onClick={handleBack} variant="contained">
          Back to Tasks
        </Button>
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            Task Details
          </Typography>
          <IconButton onClick={handleRefresh} sx={{ ml: 'auto' }}>
            <Refresh />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip 
            label={task.task_status?.status || 'Unknown Status'} 
            color={getStatusColor(task.task_status?.status)}
            size="medium"
          />
          <Chip 
            label={task.task_type?.type || 'Unknown Type'} 
            color="info"
            size="medium"
          />
          
          {/* Task Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {/* Take Task Button - only show if task is unassigned */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AssignmentInd />}
              onClick={handleTakeTask}
              disabled={!isTaskUnassigned()}
              sx={{
                opacity: isTaskUnassigned() ? 1 : 0.5,
                cursor: isTaskUnassigned() ? 'pointer' : 'not-allowed'
              }}
            >
              Take
            </Button>
            
            {task.task_status?.status !== 'COMPLETED' && task.task_status?.status !== 'CANCELLED' && (
              <>
                <Tooltip 
                  title={
                    isWorkflowWithChecklist(task) && !canCompleteWorkflowTask(task) 
                      ? "Complete all checklist items before finishing this task" 
                      : ""
                  }
                  arrow
                >
                  <span>
                    {/* <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={
                        (task.task_type?.type === 'MOVE_TRAILER' ? completingMovement : completing) ? 
                        <CircularProgress size={16} color="inherit" /> : 
                        (task.task_type?.type === 'MOVE_TRAILER' ? <DirectionsCar /> : <CheckCircle />)
                      }
                      onClick={() => {
                        if (task.task_type?.type === 'MOVE_TRAILER') {
                          handleCompleteMovementTask();
                        } else {
                          handleCompleteTask();
                        }
                      }}
                      disabled={
                        completing || completingMovement || cancelling || 
                        (isWorkflowWithChecklist(task) && !canCompleteWorkflowTask(task))
                      }
                    >
                      {task.task_type?.type === 'MOVE_TRAILER' ? 
                        (completingMovement ? 'Completing...' : 'Complete') : 
                        (completing ? 'Completing...' : 'Complete Task')
                      }
                    </Button> */}
                  </span>
                </Tooltip>
                
                {/* <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
                  onClick={handleCancelTask}
                  disabled={cancelling || completing || completingMovement}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Task'}
                </Button> */}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Task Information */}
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Task ID
                </Typography>
                <Typography variant="body1">
                  {task.uuid}
                </Typography>
              </Box>
              

              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body1">
                  {task.task_type?.type || 'Unknown'} ({task.task_type?.class || 'Unknown Class'})
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Facility ID
                </Typography>
                <Typography variant="body1">
                  {task.facility_id}
                </Typography>
              </Box>
              
              {task.driver && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Driver Name
                  </Typography>
                  <Typography variant="body1">
                    {task.driver.firstName} {task.driver.lastName}
                  </Typography>
                </Box>
              )}

              {task.modified_by_user && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completed By User
                  </Typography>
                  <Typography variant="body1">
                    {task.modified_by_user.email}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Movement Information - Only for MOVE_TRAILER tasks */}
        {task.task_type?.type === 'MOVE_TRAILER' && (task.from_object || task.to_object) && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Movement Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {task.from_object && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      From Location
                    </Typography>
                    <Typography variant="body1">
                      {('name' in task.from_object ? task.from_object.name : task.from_object.number)} ({task.from_location || 'Unknown'})
                      {'facilityZoneUuid' in task.from_object && task.from_object.facilityZoneUuid && getZoneName(task.from_object.facilityZoneUuid) && (
                        <Typography component="span" sx={{ ml: 1 }}>
                          - Zone: {getZoneName(task.from_object.facilityZoneUuid)}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.from_object.uuid}
                    </Typography>
                  </Box>
                )}
                
                {task.to_object && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      To Location
                    </Typography>
                    <Typography variant="body1">
                      {('name' in task.to_object ? task.to_object.name : task.to_object.number)} ({task.to_location || 'Unknown'})
                      {'facilityZoneUuid' in task.to_object && task.to_object.facilityZoneUuid && getZoneName(task.to_object.facilityZoneUuid) && (
                        <Typography component="span" sx={{ ml: 1 }}>
                          - Zone: {getZoneName(task.to_object.facilityZoneUuid)}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.to_object.uuid}
                    </Typography>
                  </Box>
                )}
                
                {task.trailer && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Trailer & SCAC
                    </Typography>
                    <Typography variant="body1">
                      Trailer: {task.trailer.trailer_number}
                    </Typography>
                    <Typography variant="body1">
                      SCAC: {task.trailer.carrier?.name || 'N/A'}
                    </Typography>
                  </Box>
                )}
                
                {task.from_object && task.to_object && (
                  <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="medium" align="center">
                      {('name' in task.from_object ? task.from_object.name : task.from_object.number)} → {('name' in task.to_object ? task.to_object.name : task.to_object.number)}
                      {task.trailer && (
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          Trailer: {task.trailer.trailer_number} | SCAC: {task.trailer.carrier?.name || 'N/A'}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Workflow Information - Only for WORKFLOW tasks */}
        {task.task_type?.type === 'WORKFLOW' && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Workflow Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {task.data?.workflow_type && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Workflow Type
                    </Typography>
                    <Typography variant="body1">
                      {formatWorkflowType(task.data.workflow_type)}
                    </Typography>
                  </Box>
                )}
                
                {task.data?.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {task.data.description}
                    </Typography>
                  </Box>
                )}
                
                {isWorkflowWithChecklist(task) && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Checklist ({task.data.checklist.length} items)
                      </Typography>
                      <Chip 
                        label={`${checklistCompletionState[task.uuid] ? 
                          task.data.checklist.length : 0}/${task.data.checklist.length} completed`}
                        size="small"
                        color={canCompleteWorkflowTask(task) ? "success" : "warning"}
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Single checkbox for completing all checklist items */}
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checklistCompletionState[task.uuid] || false}
                            onChange={(e) => handleChecklistChange(task.uuid, e.target.checked)}
                            size="small"
                            color="primary"
                          />
                        }
                        label={
                          <Typography 
                            variant="body2"
                            sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 'bold'
                            }}
                          >
                            Mark all checklist items as complete
                          </Typography>
                        }
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          ml: 0
                        }}
                      />
                    </Box>
                    
                    {/* Display checklist items for reference */}
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Checklist Items:
                      </Typography>
                      {task.data.checklist.map((item: string, index: number) => (
                        <Typography 
                          key={index}
                          variant="body2"
                          sx={{ 
                            fontSize: '0.875rem',
                            mb: 0.5,
                            pl: 2,
                            color: 'text.secondary'
                          }}
                        >
                          • {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Timestamps */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timestamps
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {formatDate(task.created_at)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1">
                  {formatDate(task.updated_at)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Due At
                </Typography>
                <Typography variant="body1">
                  {formatDate(task.due_at)}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Completed At
                </Typography>
                <Typography variant="body1">
                  {formatDate(task.completed_at)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Task Data */}
        {task.data && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Task Data
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Paper 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'grey.50', 
                    maxHeight: '300px', 
                    overflow: 'auto' 
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(task.data, null, 2)}
                  </pre>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}



        {/* Metadata */}
        {task.metadata && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Metadata
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Paper 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'grey.50', 
                    maxHeight: '300px', 
                    overflow: 'auto' 
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(task.metadata, null, 2)}
                  </pre>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* User IDs */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {task.user_id && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Assigned User ID
                      </Typography>
                      <Typography variant="body1">
                        {task.user_id}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                {task.completed_by_user_id && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Completed By User ID
                      </Typography>
                      <Typography variant="body1">
                        {task.completed_by_user_id}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                {task.completed_by_driver_id && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Completed By Driver ID
                      </Typography>
                      <Typography variant="body1">
                        {task.completed_by_driver_id}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                
                {task.modified_by_user_id && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Modified By User ID
                      </Typography>
                      <Typography variant="body1">
                        {task.modified_by_user_id}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Movement Task Completion Modal */}
      <MovementTaskCompletionModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setTaskToComplete(null);
        }}
        task={taskToComplete}
        onConfirm={handleCompleteMovementTaskWithDestination}
        requireSpecificDestination={!!taskToComplete?.requires_destination_selection}
        facilities={taskDetailFacilities}
        facilityZones={taskDetailFacilityZones}
        yardSpots={taskDetailYardSpots}
        warehouseZones={taskDetailWarehouseData?.warehouseZones || []}
        docks={taskDetailWarehouseData?.warehouseZones?.flatMap((zone: any) => 
          (zone.docks || []).map((dock: any) => ({
          uuid: dock.dockUuid,
          id: dock.dockUuid,
          name: dock.dockName,
          display_name: dock.dockName,
          door_name: dock.dockName,
          trailer_name: dock.trailer_name,
          warehouse_zone_uuid: zone.zoneUuid,
          warehouse_zone_name: zone.zoneName
        }))
        ) || []}
      />
    </div>
  );
};

export default TaskDetail; 